<script lang="ts">
import { computed, defineComponent, unref, watch } from 'vue';

import {
  makeDisabledProps,
  makeIconProps,
  makeLinkProps,
  makeTagProps,
  useDisabled,
  useIcon,
  useLink,
  useNestedItem
} from '../../composable';
import { CdsIcon } from '../CdsIcon';

import { useList } from './composable';

import type { IItemSlotProps } from '../../composable';
import type { PropType } from 'vue';

export const componentName = 'CdsListItem';
export default defineComponent({
  name: componentName,
  components: { CdsIcon },
  inheritAttrs: false,
  props: {
    /**
     * Заголовок элемента списка
     */
    title: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Описание элемента списка
     */
    description: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Значение элемента списка
     */
    value: {
      type: [String, Number, Object, Array, Boolean] as PropType<any>,
      default: null
    },
    /**
     * Активное состояние
     */
    active: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Принудительно сделать элемент списка кликабельным
     */
    isClickable: {
      type: Boolean as PropType<boolean>,
      default: false
    },

    ...makeIconProps(),
    ...makeLinkProps(),
    ...makeTagProps(),
    ...makeDisabledProps()
  },
  emits: {
    /**
     * Событие выбора элемента списка
     * @type { MouseEvent | KeyboardEvent | TouchEvent }
     */
    click: (event: MouseEvent | KeyboardEvent | TouchEvent) => true
  },
  setup(props, { slots, emit, expose }) {
    const list = useList();
    const { href, to, ...link } = useLink(props, emit);
    const id = computed(() => props.value ?? unref(href));
    const { select, isSelected, isIndeterminate, isGroupActivator, root, parent, openOnSelect } = useNestedItem(
      id,
      false
    );
    const { disabledClasses } = useDisabled(props);
    const { hasPrepend, hasAppend } = useIcon(props, slots, componentName);
    const isActive = computed(
      () => props.active !== false && (props.active || unref(link.isActive) || unref(isSelected))
    );
    const isLink = computed(() => unref(link.isLink));
    const isClickable = computed(
      () => props.isClickable || (!props.disabled && (unref(link.isLink) || (props.value != null && !!list)))
    );
    const internalTag = computed(() => (unref(isLink) ? unref(link.tag) : 'div'));
    const hasTitle = computed(() => !!slots.title || !!props.title);
    const hasDescription = computed(() => !!slots.description || !!props.description);

    const slotProps = computed<IItemSlotProps>(() => ({
      isActive: unref(isActive),
      select,
      isSelected: unref(isSelected),
      isIndeterminate: unref(isIndeterminate)
    }));

    const classes = computed(() => ({
      'cds-list-item--active': unref(isActive),
      'cds-list-item--clickable': unref(isClickable),
      'cds-list-item--selected': unref(isSelected)
    }));

    watch(
      () => unref(link.isActive),
      (value) => {
        if (value && unref(parent) != null) {
          root.open(unref(parent), true);
        }

        if (value) {
          openOnSelect(value);
        }
      },
      { immediate: true }
    );

    function onClick(event: MouseEvent) {
      emit('click', event);

      if (isGroupActivator || !unref(isClickable)) return;

      link.navigate?.(event);
      props.value != null && select(!unref(isSelected), event);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick(event as any as MouseEvent);
      }
    }

    list?.updateHasPrepend(unref(hasPrepend));

    expose({
      isActive,
      isSelected,
      isIndeterminate,
      select
    });

    return {
      internalTag,
      href,
      to,
      slotProps,
      hasTitle,
      hasDescription,
      hasPrepend,
      hasAppend,
      classes,
      disabledClasses,
      tabindex: unref(isClickable) ? 0 : undefined,
      onClick,
      onKeyDown: unref(isClickable) && !unref(isLink) && onKeyDown
    };
  }
});
</script>

<template>
  <component :is="tag" class="cds-list-wrapper">
    <component
      :is="internalTag"
      :to="to"
      :href="href"
      :class="[classes, disabledClasses]"
      :tabindex="tabindex"
      class="cds-list-item"
      v-bind="$attrs"
      @click="onClick"
      @keydown="onKeyDown"
    >
      <div v-if="hasPrepend" class="cds-list-item__prepend">
        <!--
        @slot Содержимое в начале элемента
        @type string | html
      -->
        <slot name="prepend" v-bind="slotProps" />
        <cds-icon v-if="!!prependIcon" :name="prependIcon" />
      </div>
      <div class="cds-list-item__content" data-no-activator="">
        <div v-if="hasTitle" class="cds-list-item-title">
          <!--
          @slot Название элемента списка
          @type string | html
        -->
          <slot name="title">
            {{ title }}
          </slot>
        </div>
        <div v-if="hasDescription" class="cds-list-item-description">
          <!--
          @slot Описание элемента списка
          @type string | html
        -->
          <slot name="description">
            {{ description }}
          </slot>
        </div>
        <!--
        @slot Значение элемента списка
        @type string | html
      -->
        <slot />
      </div>
      <div v-if="hasAppend" class="cds-list-item__append">
        <!--
        @slot Содержимое в конце элемента списка
        @type string | html
      -->
        <slot name="append" v-bind="slotProps" />
        <cds-icon v-if="!!appendIcon" :name="appendIcon" />
      </div>
    </component>
  </component>
</template>
